import { inspectVideoTrack, measureVideoFrameRate } from './fps.js';

export class CameraController {
  constructor() {
    this.video = null;
    this.stream = null;
    this.track = null;
    this._frameHandle = null;
    this._frameKind = null;
    this._onFrame = null;
    this._operationGeneration = 0;
    this._pendingStreams = new Map();
    this._releasedStreams = new WeakSet();
  }

  async list() {
    if (!navigator.mediaDevices?.enumerateDevices) return [];
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter((device) => device.kind === 'videoinput').map((device, index) => ({
      id: device.deviceId,
      label: device.label || `Caméra ${index + 1}`,
      groupId: device.groupId || null,
    }));
  }

  async open(video, {
    deviceId,
    facingMode = 'environment',
    width = 1280,
    height = 720,
    frameRate = 60,
    fallbackFromStaleDevice = true,
    // Safari/iOS can fail one physical camera while another remains usable.
    // Only recoverable device errors may advance through this bounded plan.
    fallbackOnRecoverableError = true,
    facingModeFallbacks,
  } = {}) {
    const operationGeneration = ++this._operationGeneration;
    this._releaseCurrentStream();
    this._releasePendingStreams();
    if (!navigator.mediaDevices?.getUserMedia) throw new Error('Caméra Web indisponible ou page non sécurisée');
    const commonConstraints = {
      width: { ideal: width },
      height: { ideal: height },
      frameRate: { ideal: frameRate },
    };
    const facingPlan = buildFacingPlan(facingMode, facingModeFallbacks);
    const attempts = [];
    let stream = null;
    let selectedConstraint = null;
    let fellBackFromDeviceId = false;
    const isCurrentOperation = () => this._operationGeneration === operationGeneration;
    const requestOptions = {
      isCurrentOperation,
      releaseStream: (candidateStream) => this._releaseStream(candidateStream),
    };

    if (deviceId) {
      const exactSelection = { type: 'deviceId', facingMode: null };
      try {
        stream = await requestCameraStream(commonConstraints, exactSelection, attempts, {
          ...requestOptions,
          deviceId,
        });
        selectedConstraint = exactSelection;
      } catch (error) {
        if (isStaleCameraOperationError(error)) {
          throw attachCameraFailureDiagnostics(error, {
            deviceId,
            facingMode,
            attempts,
            fellBackFromDeviceId,
          });
        }
        const mayDropStaleId = isStaleDeviceError(error) && fallbackFromStaleDevice;
        const mayRecoverHardware = !isStaleDeviceError(error)
          && fallbackOnRecoverableError
          && isRecoverableCameraError(error);
        if (!mayDropStaleId && !mayRecoverHardware) {
          throw attachCameraFailureDiagnostics(error, {
            deviceId,
            facingMode,
            attempts,
            fellBackFromDeviceId,
          });
        }
        fellBackFromDeviceId = true;
      }
    }

    if (!stream) {
      for (let index = 0; index < facingPlan.length; index += 1) {
        const candidate = facingPlan[index];
        const selection = candidate === null
          ? { type: 'generic', facingMode: null }
          : { type: 'facingMode', facingMode: candidate };
        try {
          stream = await requestCameraStream(commonConstraints, selection, attempts, requestOptions);
          selectedConstraint = selection;
          break;
        } catch (error) {
          if (isStaleCameraOperationError(error)) {
            throw attachCameraFailureDiagnostics(error, {
              deviceId,
              facingMode,
              attempts,
              fellBackFromDeviceId,
            });
          }
          const hasAnotherCandidate = index + 1 < facingPlan.length;
          if (!fallbackOnRecoverableError || !hasAnotherCandidate || !isRecoverableCameraError(error)) {
            throw attachCameraFailureDiagnostics(error, {
              deviceId,
              facingMode,
              attempts,
              fellBackFromDeviceId,
            });
          }
        }
      }
    }

    const track = stream?.getVideoTracks?.()[0] || null;
    if (!track) {
      this._releaseStream(stream);
      throw new Error('Aucune piste vidéo négociée');
    }
    this._pendingStreams.set(operationGeneration, { stream, video });
    video.srcObject = stream;
    try {
      await video.play();
    } catch (error) {
      this._releaseLocalStream(operationGeneration, stream, video);
      if (!isCurrentOperation()) throw staleCameraOperationError();
      throw error;
    }
    if (!isCurrentOperation()) {
      this._releaseLocalStream(operationGeneration, stream, video);
      throw staleCameraOperationError();
    }
    this._pendingStreams.delete(operationGeneration);
    this.stream = stream;
    this.track = track;
    this.video = video;
    const applied = readAppliedCameraSelection(track, selectedConstraint);
    const appliedFacingMismatch = selectedConstraint?.type !== 'deviceId'
      && applied.facingMode !== null
      && applied.facingMode !== facingMode;
    const fellBackFromFacingMode = selectedConstraint?.type === 'generic'
      || (selectedConstraint?.type === 'facingMode' && selectedConstraint.facingMode !== facingMode)
      || appliedFacingMismatch;
    const failedAttempts = attempts.filter((attempt) => !attempt.ok);
    const fallbackReason = failedAttempts[0]?.errorName
      || (appliedFacingMismatch ? 'APPLIED_FACING_MISMATCH' : null);
    return {
      ...this.describe(),
      requestedDeviceId: deviceId || null,
      requestedFacingMode: facingMode,
      appliedDeviceId: applied.deviceId,
      appliedFacingMode: applied.facingMode,
      fellBackFromDeviceId,
      fellBackFromFacingMode,
      fallbackReason,
      cameraSelection: {
        requested: {
          deviceId: deviceId || null,
          facingMode,
        },
        applied,
        fallback: {
          used: fellBackFromDeviceId || fellBackFromFacingMode,
          fromDeviceId: fellBackFromDeviceId,
          fromFacingMode: fellBackFromFacingMode,
          reason: fallbackReason,
        },
        attempts,
      },
    };
  }

  describe() {
    const declared = inspectVideoTrack(this.track);
    return {
      label: this.track?.label || 'Caméra',
      ...declared,
    };
  }

  start(onFrame) {
    this.stop();
    this._onFrame = onFrame;
    if (typeof this.video?.requestVideoFrameCallback === 'function') {
      this._frameKind = 'requestVideoFrameCallback';
      const tick = (now, metadata) => {
        if (!this._onFrame) return;
        this._onFrame(now, metadata);
        this._frameHandle = this.video.requestVideoFrameCallback(tick);
      };
      this._frameHandle = this.video.requestVideoFrameCallback(tick);
      return this._frameKind;
    }
    this._frameKind = 'requestAnimationFrame';
    let previousTime = -1;
    const tick = (now) => {
      if (!this._onFrame) return;
      if (this.video.currentTime !== previousTime) {
        previousTime = this.video.currentTime;
        this._onFrame(now, { mediaTime: previousTime, presentedFrames: null });
      }
      this._frameHandle = requestAnimationFrame(tick);
    };
    this._frameHandle = requestAnimationFrame(tick);
    return this._frameKind;
  }

  stop() {
    this._onFrame = null;
    if (this._frameHandle !== null) {
      if (this._frameKind === 'requestVideoFrameCallback' && typeof this.video?.cancelVideoFrameCallback === 'function') {
        this.video.cancelVideoFrameCallback(this._frameHandle);
      } else if (this._frameKind === 'requestAnimationFrame') {
        cancelAnimationFrame(this._frameHandle);
      }
    }
    this._frameHandle = null;
    this._frameKind = null;
  }

  async measureCurrentFps({ durationMs = 2200, signal } = {}) {
    if (!this.video || !this.track) throw new Error('Caméra fermée');
    return measureVideoFrameRate(this.video, { track: this.track, durationMs, maxFrames: Infinity, signal });
  }

  // getCapabilities() does not expose resolution/FPS interactions. Probe a
  // bounded set and rank the actually delivered frame cadence, never the
  // requested or merely configured value.
  async benchmarkMaximumFps({ durationMs = 900, onProgress = () => {}, signal } = {}) {
    if (!this.video || !this.track) throw new Error('Caméra fermée');
    const declared = inspectVideoTrack(this.track);
    const cases = buildFpsBenchmarkCandidates(declared);
    const results = [];

    for (let index = 0; index < cases.length; index++) {
      if (signal?.aborted) throw abortError();
      const candidate = cases[index];
      onProgress({ index, total: cases.length, candidate });
      try {
        await this.track.applyConstraints({
          width: { ideal: candidate.width },
          height: { ideal: candidate.height },
          frameRate: { ideal: candidate.requestedFps },
        });
        await settleFrames(this.video, 3, signal);
        const appliedSettings = inspectVideoTrack(this.track).settings;
        const measurement = await measureVideoFrameRate(this.video, {
          track: this.track,
          durationMs,
          maxFrames: Infinity,
          signal,
        });
        results.push({ ...candidate, ok: true, appliedSettings, measurement });
      } catch (error) {
        results.push({ ...candidate, ok: false, error: error?.message || String(error) });
      }
    }

    const best = rankFpsBenchmarkResults(results);
    const peak = rankPeakFpsBenchmarkResults(results);
    const selected = best || peak;
    if (selected) {
      await this.track.applyConstraints({
        width: { ideal: selected.width },
        height: { ideal: selected.height },
        frameRate: { ideal: selected.requestedFps },
      }).catch(() => {});
    }
    return { declared, cases: results, best, peak };
  }

  async benchmarkTorch({ cycles = 4, signal } = {}) {
    if (!this.track) throw new Error('Caméra fermée');
    let capabilities = {};
    try { capabilities = this.track.getCapabilities?.() || {}; } catch {}
    if (!capabilities.torch) return { available: false, reason: 'La piste ne publie pas la contrainte torch' };
    const latencies = [];
    try {
      for (let index = 0; index < cycles * 2; index++) {
        if (signal?.aborted) throw abortError();
        const enabled = index % 2 === 0;
        const started = performance.now();
        await this.track.applyConstraints({ advanced: [{ torch: enabled }] });
        latencies.push(performance.now() - started);
      }
    } finally {
      await this.track.applyConstraints({ advanced: [{ torch: false }] }).catch(() => {});
    }
    return {
      available: true,
      latenciesMs: latencies,
      ...summarizeTorchTimings(latencies),
    };
  }

  async close() {
    this._operationGeneration += 1;
    this._releaseCurrentStream();
    this._releasePendingStreams();
  }

  _releaseCurrentStream() {
    this.stop();
    const stream = this.stream;
    const video = this.video;
    this.stream = null;
    this.track = null;
    this.video = null;
    this._releaseStream(stream);
    if (video?.srcObject === stream) video.srcObject = null;
  }

  _releasePendingStreams() {
    for (const [generation, pending] of this._pendingStreams) {
      this._pendingStreams.delete(generation);
      this._releaseStream(pending.stream);
      if (pending.video?.srcObject === pending.stream) pending.video.srcObject = null;
    }
  }

  _releaseLocalStream(generation, stream, video) {
    const pending = this._pendingStreams.get(generation);
    if (pending?.stream === stream) this._pendingStreams.delete(generation);
    this._releaseStream(stream);
    if (video?.srcObject === stream) video.srcObject = null;
  }

  _releaseStream(stream) {
    if (!stream || (typeof stream !== 'object' && typeof stream !== 'function')) return;
    if (this._releasedStreams.has(stream)) return;
    this._releasedStreams.add(stream);
    for (const track of stream.getTracks?.() || []) {
      try { track.stop(); } catch {}
    }
  }
}

function isStaleDeviceError(error) {
  return ['NotFoundError', 'OverconstrainedError'].includes(error?.name);
}

function isRecoverableCameraError(error) {
  return [
    'AbortError',
    'NotFoundError',
    'NotReadableError',
    'OverconstrainedError',
    'SourceUnavailableError',
    'TrackStartError',
  ].includes(error?.name);
}

function defaultFacingModeFallbacks(facingMode) {
  return facingMode === 'environment' ? [null] : ['environment', null];
}

function buildFacingPlan(facingMode, configuredFallbacks) {
  if (typeof facingMode !== 'string' || !facingMode) throw new TypeError('facingMode invalide');
  const fallbacks = configuredFallbacks === undefined
    ? defaultFacingModeFallbacks(facingMode)
    : configuredFallbacks;
  if (!Array.isArray(fallbacks)) throw new TypeError('facingModeFallbacks doit être un tableau');
  const plan = [];
  const seen = new Set();
  for (const candidate of [facingMode, ...fallbacks]) {
    if (candidate !== null && (typeof candidate !== 'string' || !candidate)) {
      throw new TypeError('fallback facingMode invalide');
    }
    const key = candidate === null ? 'generic' : `facing:${candidate}`;
    if (seen.has(key)) continue;
    seen.add(key);
    plan.push(candidate);
  }
  return plan;
}

async function requestCameraStream(commonConstraints, selection, attempts, {
  deviceId = null,
  isCurrentOperation = () => true,
  releaseStream = stopMediaStream,
} = {}) {
  const video = selection.type === 'deviceId'
    ? { ...commonConstraints, deviceId: { exact: deviceId } }
    : selection.type === 'generic'
      ? { ...commonConstraints }
      : { ...commonConstraints, facingMode: { ideal: selection.facingMode } };
  const diagnostic = {
    type: selection.type,
    facingMode: selection.facingMode,
    ok: false,
    errorName: null,
    errorMessage: null,
  };
  attempts.push(diagnostic);
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video, audio: false });
    if (!isCurrentOperation()) {
      releaseStream(stream);
      throw staleCameraOperationError();
    }
    diagnostic.ok = true;
    return stream;
  } catch (error) {
    if (!isCurrentOperation() && !isStaleCameraOperationError(error)) {
      const staleError = staleCameraOperationError();
      staleError.cause = error;
      diagnostic.errorName = staleError.name;
      diagnostic.errorMessage = staleError.message;
      diagnostic.supersededErrorName = error?.name || 'Error';
      throw staleError;
    }
    diagnostic.errorName = error?.name || 'Error';
    diagnostic.errorMessage = error?.message || String(error);
    throw error;
  }
}

function staleCameraOperationError() {
  const error = new Error('Ouverture caméra remplacée par une opération plus récente');
  error.name = 'AbortError';
  error.code = 'CAMERA_OPERATION_STALE';
  return error;
}

function isStaleCameraOperationError(error) {
  return error?.name === 'AbortError' && error?.code === 'CAMERA_OPERATION_STALE';
}

function stopMediaStream(stream) {
  for (const track of stream?.getTracks?.() || []) {
    try { track.stop(); } catch {}
  }
}

function readAppliedCameraSelection(track, selectedConstraint) {
  let settings = {};
  try { settings = track?.getSettings?.() || {}; } catch {}
  return {
    deviceId: typeof settings.deviceId === 'string' && settings.deviceId ? settings.deviceId : null,
    facingMode: typeof settings.facingMode === 'string' && settings.facingMode
      ? settings.facingMode
      : null,
    constraint: selectedConstraint?.type || null,
    constraintFacingMode: selectedConstraint?.facingMode || null,
  };
}

function attachCameraFailureDiagnostics(error, {
  deviceId,
  facingMode,
  attempts,
  fellBackFromDeviceId,
}) {
  try {
    error.cameraSelection = {
      requested: {
        deviceId: deviceId || null,
        facingMode,
      },
      applied: null,
      fallback: {
        used: fellBackFromDeviceId || attempts.length > 1,
        fromDeviceId: fellBackFromDeviceId,
        fromFacingMode: attempts.some((attempt) => attempt.type === 'facingMode'
          && attempt.facingMode !== facingMode) || attempts.some((attempt) => attempt.type === 'generic'),
        reason: attempts.find((attempt) => !attempt.ok)?.errorName || error?.name || 'Error',
      },
      attempts,
    };
  } catch {}
  return error;
}

export function rankFpsBenchmarkResults(results) {
  const successful = results
    .filter((result) => result.ok
      && result.measurement?.stability?.stable === true
      && Number.isFinite(result.measurement?.deliveredFps))
    .sort((a, b) => {
      const stableDelta = Number(b.measurement.stability?.stable) - Number(a.measurement.stability?.stable);
      if (stableDelta) return stableDelta;
      const fpsDelta = b.measurement.deliveredFps - a.measurement.deliveredFps;
      if (fpsDelta) return fpsDelta;
      const lossA = Number.isFinite(a.measurement.loss?.rate) ? a.measurement.loss.rate : 1;
      const lossB = Number.isFinite(b.measurement.loss?.rate) ? b.measurement.loss.rate : 1;
      return lossA - lossB;
    });
  return successful[0] || null;
}

export function rankPeakFpsBenchmarkResults(results) {
  return results
    .filter((result) => result.ok && Number.isFinite(result.measurement?.deliveredFps))
    .sort((a, b) => b.measurement.deliveredFps - a.measurement.deliveredFps)[0] || null;
}

export function buildFpsBenchmarkCandidates(declared = {}) {
  const capMax = declared.capabilities?.frameRate?.max || null;
  const settings = declared.settings || {};
  const rates = uniqueNumbers([capMax, 240, 120, 90, 60, 30])
    .filter((rate) => !capMax || rate <= capMax + 0.01);
  const resolutions = uniqueResolutions([
    { width: settings.width || 1280, height: settings.height || 720 },
    { width: 1280, height: 720 },
    { width: 640, height: 480 },
  ]);
  return resolutions.flatMap((resolution) => rates.map((rate) => ({
    ...resolution,
    requestedFps: rate,
  })));
}

export function summarizeTorchTimings(latencies, cOokClockRequirementHz = 2200) {
  const sorted = latencies.filter((value) => Number.isFinite(value) && value >= 0).sort((a, b) => a - b);
  if (!sorted.length) throw new RangeError('aucune latence torch valide');
  const p50Ms = percentile(sorted, 0.5);
  const p95Ms = percentile(sorted, 0.95);
  const toggleUpperBoundHz = p95Ms > 0 ? 1000 / (2 * p95Ms) : Infinity;
  return {
    p50Ms,
    p95Ms,
    toggleUpperBoundHz,
    cOokClockRequirementHz,
    apiTimingCandidate: toggleUpperBoundHz >= cOokClockRequirementHz,
  };
}

export function measureMonotonicClockResolution(
  clock = globalThis.performance?.now?.bind(globalThis.performance),
  samples = 20_000,
) {
  if (typeof clock !== 'function' || !Number.isInteger(samples) || samples < 1) return null;
  let previous = Number(clock());
  let minimumPositiveDelta = Infinity;
  for (let index = 0; index < samples; index++) {
    const current = Number(clock());
    const delta = current - previous;
    if (Number.isFinite(delta) && delta > 0) minimumPositiveDelta = Math.min(minimumPositiveDelta, delta);
    previous = current;
  }
  return Number.isFinite(minimumPositiveDelta) ? minimumPositiveDelta : null;
}

function uniqueNumbers(values) {
  return [...new Set(values.filter((value) => Number.isFinite(value) && value > 0).map((value) => Math.round(value * 100) / 100))];
}
function uniqueResolutions(values) {
  const seen = new Set();
  return values.filter(({ width, height }) => {
    const key = `${width}x${height}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
function percentile(sorted, ratio) {
  if (!sorted.length) return null;
  return sorted[Math.min(sorted.length - 1, Math.ceil(sorted.length * ratio) - 1)];
}
function abortError() {
  const error = new Error('Mesure annulée');
  error.name = 'AbortError';
  return error;
}
function settleFrames(video, count, signal) {
  return new Promise((resolve, reject) => {
    let remaining = count;
    const tick = () => {
      if (signal?.aborted) return reject(abortError());
      remaining -= 1;
      if (remaining <= 0) return resolve();
      if (typeof video.requestVideoFrameCallback === 'function') video.requestVideoFrameCallback(tick);
      else requestAnimationFrame(tick);
    };
    if (typeof video.requestVideoFrameCallback === 'function') video.requestVideoFrameCallback(tick);
    else requestAnimationFrame(tick);
  });
}
